import { t as uiText } from '../../i18n';
import React, { memo } from 'react';
import ReactDOM from 'react-dom';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { FolderData } from '../../types/folder';

interface FolderItemProps {
  folder: FolderData;
  folders: FolderData[];
  folderPageCounts?: Record<string, number>;
  level: number;
  openFolderIds: Set<string>;
  selectedFolderId: string | null;
  onToggle?: (folderId: string) => void;
  onSelect: (folderId: string) => void;
  onMenuClick?: (e: React.MouseEvent, folderId: string) => void;
  onMenuAction?: (action: string, folderId: string) => void;
  openFolderMenuId?: string | null;
  // 編集関連のprops
  newFolderName?: string;
  onNameChange?: (name: string) => void;
  onSaveName?: (folderId: string) => void;
  onKeyPress?: (e: React.KeyboardEvent, folderId: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  // ドラッグ用の新規props
  isDragging?: boolean;
  draggedId?: string | null;
  wouldCreateCircularReference?: (draggedId: string, targetId: string, folders: FolderData[]) => boolean;
  // 選択モード用
  showSelectionMode?: boolean;
}

const FolderItem = ({ 
  folder, 
  folders, 
  folderPageCounts,
  level, 
  openFolderIds, 
  selectedFolderId, 
  onToggle, 
  onSelect,
  onMenuClick,
  onMenuAction,
  openFolderMenuId,
  newFolderName,
  onNameChange,
  onSaveName,
  onKeyPress,
  inputRef,
  // ドラッグ用
  isDragging: globalIsDragging = false,
  draggedId,
  wouldCreateCircularReference,
  showSelectionMode = false,
}: FolderItemProps) => {
  const childFolders = folders
    .filter(f => f.parentId === folder.id)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const folderPageCount = folderPageCounts?.[folder.id] || 0;

  const isOpen = openFolderIds.has(folder.id);
  const isSelected = selectedFolderId === folder.id;
  const isMenuOpen = openFolderMenuId === folder.id;
  
  // refs / state for menu positioning
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  // 基本座標は getBoundingClientRect() の値（viewport基準）をそのまま入れる
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number } | null>(null);
  // オーバーフロー補正フラグ
  const [menuStyleAdjust, setMenuStyleAdjust] = React.useState<{ alignRight: boolean; moveUp: boolean }>({ alignRight: true, moveUp: false });

  // ドラッグ機能
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging: isSelfDragging,
  } = useSortable({ 
    id: folder.id,
    disabled: folder.isEditing || showSelectionMode // 編集中または選択モードでは無効
  });

  // 循環参照チェック
  const isInvalid = draggedId && wouldCreateCircularReference 
    ? wouldCreateCircularReference(draggedId, folder.id, folders) 
    : false;

  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: folder.id,
    disabled: isSelfDragging || showSelectionMode, // 自分自身をドラッグ中または選択モードでは無効
    data: {
      type: 'folder',
      isInvalid: isInvalid
    }
  });

  const dragRef = setSortableRef;

  const dragStyle = isSelfDragging ? {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: 0.5,
    cursor: 'grabbing',
  } : {
    backgroundColor: isOver 
      ? (isInvalid ? '#f8d7da' : '#f0e6d8') 
      : 'transparent',
    border: isOver 
      ? (isInvalid ? '2px dashed #dc3545' : '2px dashed var(--workspace-primary, #cfae91)')
      : '2px solid transparent',
    cursor: isOver && isInvalid ? 'not-allowed' : 'grab',
  };

  // メニュー開閉時：まずボタンの位置を取得（viewport基準）
  React.useEffect(() => {
    if (isMenuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // position: fixed を使うため viewport 基準のまま top/left を保持する
      setMenuPos({ top: rect.bottom, left: rect.right });
      // 初期は右揃え（menu の右端 = rect.right）で表示する想定
      setMenuStyleAdjust({ alignRight: true, moveUp: false });
    } else if (!isMenuOpen) {
      // 閉じたらリセット
      setMenuPos(null);
      setMenuStyleAdjust({ alignRight: true, moveUp: false });
    }
  }, [isMenuOpen]);

  // メニュー描画後にオーバーフローしていれば補正する（左はみ出し / 下はみ出し）
  React.useLayoutEffect(() => {
    if (!isMenuOpen || !menuPos || !menuRef.current) return;

    const mRect = menuRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    let alignRight = true; // 右揃え（transform: translateX(-100%))
    let moveUp = false;

    // 左にはみ出しているか? （右揃えの前提でメニュー左端 = menuPos.left - menuWidth）
    if (menuPos.left - mRect.width < 0) {
      // 右揃えだと左にはみ出す -> 左揃えに切替
      alignRight = false;
    }

    // 下にはみ出しているか?
    if (menuPos.top + mRect.height > viewportH) {
      // メニューの上側に表示（button の上に出す）
      moveUp = true;
    }

    setMenuStyleAdjust({ alignRight, moveUp });
  }, [isMenuOpen, menuPos]);

  // デバッグログ（必要なら出して下さい）
  // console.log('menuPos', menuPos, 'adjust', menuStyleAdjust);

  return (
    <div ref={dragRef} style={dragStyle}>
      <div style={{ marginLeft: `${level * 4}px`, width: `calc(100% - ${level * 4}px)` }}>
        <div style={{ position: 'relative' }}>
          <div className="folder-item-container">
          {folder.isEditing ? (
            (() => {
              return (
                <input
                  ref={inputRef}
                  type="text"
                  value={newFolderName}
                  onChange={(e) => onNameChange?.(e.target.value)}
                  onKeyPress={(e) => onKeyPress?.(e, folder.id)}
                  onBlur={() => onSaveName?.(folder.id)}
                  placeholder={uiText("Enter folder name")}
                  className="folder-name-input"
                  style={{ flex: 1, padding: '6px 8px', fontSize: '14px' }}
                />
              );
            })()
          ) : (
            (<div 
              className="folder-item folder-menu-container"
              style={{
                pointerEvents: 'auto' as const,
                display: 'grid',
                gridTemplateColumns: '1fr 24px', // フォルダ名用 + メニュー用固定列
                alignItems: 'center',
                width: '100%',
                gap: '4px'
              }}
            >
            {/* 左カラム: フォルダ内容 */}
            <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
              {!folder.isEditing && !showSelectionMode && (
                <button
                  className="drag-handle"
                  {...attributes}
                  {...listeners}
                  aria-label={uiText("Drag to reorder")}
                  style={{
                    cursor: isSelfDragging ? 'grabbing' : 'grab',
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: '1px',
                    marginRight: '4px',
                    borderRadius: '2px',
                    transition: 'background-color 0.2s ease',
                    userSelect: 'none',
                    fontSize: '12px',
                    color: '#999',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--workspace-border-soft, #f5f0eb)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  ⋮⋮
                </button>
              )}
              
              <span
                className="toggle-icon"
                onClick={(e) => { e.stopPropagation(); onToggle?.(folder.id); }}
                role="button"
                tabIndex={0}
                aria-label={isOpen ? uiText("Collapse folder") : uiText("Expand folder")}
                style={{
                  cursor: 'pointer',
                  color: '#6c757d',
                  fontSize: '12px',
                  marginRight: '4px',
                  padding: '0px',
                  borderRadius: '2px',
                  transition: 'background-color 0.2s ease',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--workspace-border-soft, #f5f0eb)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
              >
                {isOpen ? '▽' : '▷'}
              </span>

              <span
                className="folder-name"
                onClick={(e) => { e.stopPropagation(); onSelect(folder.id); }}
                aria-label={uiText("Select folder \"{{name}}\"", { name: folder.name })}
                style={{
                  backgroundColor: isSelected ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                  color: isSelected ? '#fff' : '#333',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0px 2px',
                  borderRadius: '2px',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  lineHeight: '16px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: '60px',
                  maxWidth: '240px',
                  transition: 'background-color 0.2s ease, color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  height: '18px'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'rgba(207, 174, 145, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {folder.name || uiText("Untitled folder")}
                </span>
                <span
                  style={{
                    fontSize: '11px',
                    marginLeft: '6px',
                    flexShrink: 0,
                    color: isSelected ? '#fff' : '#666',
                  }}
                >
                  {folderPageCount}
                </span>
              </span>
            </div>

            {/* 右カラム: メニューボタン（常に表示、選択時のみクリック可能、選択モード時は非表示） */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <button 
                ref={buttonRef}
                onClick={(e) => { 
                  if (isSelected && !showSelectionMode) {
                    e.stopPropagation(); 
                    e.preventDefault(); 
                    onMenuClick?.(e, folder.id); 
                  }
                }}
                className="folder-menu-button"
                aria-label={uiText("Open folder menu")}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  zIndex: 99999,
                  pointerEvents: isSelected && !showSelectionMode ? 'auto' : 'none',
                  cursor: isSelected && !showSelectionMode ? 'pointer' : 'default',
                  width: '24px',
                  height: '24px',
                  color: isSelected && !showSelectionMode ? '#666' : 'transparent',
                  fontSize: '16px',
                  borderRadius: '2px',
                  transition: 'background-color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (isSelected && !showSelectionMode) {
                    e.currentTarget.style.backgroundColor = 'var(--workspace-border-soft, #f5f0eb)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isSelected && !showSelectionMode) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                ⋮
              </button>
            </div>

            {isMenuOpen && menuPos && ReactDOM.createPortal(
              <div
                ref={(el) => { menuRef.current = el; }}
                className="folder-menu-dropdown"
                // position: fixed を使い、viewport 基準の menuPos を直接使う
                style={{
                  position: 'fixed',
                  top: menuStyleAdjust.moveUp ? `${menuPos.top - 8 - (menuRef.current?.offsetHeight || 0)}px` : `${menuPos.top + 4}px`,
                  left: `${menuPos.left}px`,
                  // 右揃えか左揃えかを transform で切り替える
                  transform: menuStyleAdjust.alignRight ? 'translateX(-100%)' : 'translateX(0)',
                  backgroundColor: 'white',
                  zIndex: 999999,
                  width: 'max-content',
                  minWidth: 'max-content',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  borderRadius: '4px',
                  // pointer events は親で止めているのでここは普通に
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  onClick={(e) => { e.stopPropagation(); onMenuAction?.('add', folder.id); }}
                  className="menu-item"
                  style={{
                    backgroundColor: 'white',
                    fontSize: '14px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    color: '#333',
                    borderBottom: '1px solid #e9ecef'
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'white'; }}
                >
                  {uiText("Add folder")}
                </div>
                <div
                  onClick={(e) => { e.stopPropagation(); onMenuAction?.('rename', folder.id); }}
                  className="menu-item"
                  style={{
                    backgroundColor: 'white',
                    fontSize: '14px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    color: '#333',
                    borderBottom: '1px solid #e9ecef'
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'white'; }}
                >
                  {uiText("Rename")}
                </div>
                <div
                  onClick={(e) => { e.stopPropagation(); onMenuAction?.('delete', folder.id); }}
                  className="menu-item delete"
                  style={{
                    backgroundColor: 'white',
                    fontSize: '14px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    color: '#d32f2f'
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'white'; }}
                >
                  {uiText("Delete")}
                </div>
              </div>,
              document.body
            )}
          </div>)
        )}
          </div>
        </div>
      </div>

      <div 
        ref={setDropRef}
        className={`folder-children ${isOpen ? 'open' : 'closed'}`}
        style={{
          backgroundColor: isOver 
            ? (isInvalid ? '#f8d7da' : '#f0e6d8') 
            : 'transparent',
          border: isOver 
            ? (isInvalid ? '2px dashed #dc3545' : '2px dashed var(--workspace-primary, #cfae91)')
            : '2px solid transparent',
          borderRadius: '4px',
          transition: 'all 0.2s ease',
          minHeight: isOver ? '20px' : '0px'
        }}
      >
        {isOpen && !isSelfDragging && childFolders.map(child => (
          <FolderItem
            key={child.id}
            folder={child}
            folders={folders}
            folderPageCounts={folderPageCounts}
            level={level + 1}
            openFolderIds={openFolderIds}
            selectedFolderId={selectedFolderId}
            onToggle={onToggle}
            onSelect={onSelect}
            onMenuClick={onMenuClick}
            onMenuAction={onMenuAction}
            openFolderMenuId={openFolderMenuId}
            newFolderName={newFolderName}
            onNameChange={onNameChange}
            onSaveName={onSaveName}
            onKeyPress={onKeyPress}
            inputRef={inputRef}
            isDragging={globalIsDragging}
            draggedId={draggedId}
            wouldCreateCircularReference={wouldCreateCircularReference}
            showSelectionMode={showSelectionMode}
          />
        ))}
      </div>
    </div>
  );
};

FolderItem.displayName = 'FolderItem';
export default FolderItem;